import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Fab,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Stack,
    Alert,
    CircularProgress,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import { usePermissions } from '@contexts/PermissionContext';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    DoneAll as DoneAllIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Loop as LoopIcon
} from '@mui/icons-material';
import { colors } from '@constants/colors';
import { useAuth } from '@store/authStore';
import { apiService } from '@modules/programManager/services/apiService';
import httpClient from '@utils/httpClient';
import { formatTableDate } from '@utils/dateFormatter';


interface Workshop {
    id: number;
    title: string;
    description: string;
    assigned_to: string;
    due_date: string;
    created_at: string;
    updated_at: string;
    status: 'pending' | 'in-progress' | 'in_progress' | 'completed' | 'cancelled';
    created_by_id: string;
    created_by_role: string;
}

const WorkshopsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const { user } = useAuth();
    const { } = usePermissions();

    // Consistent button styles for View/Edit/Delete icons
    const viewButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.15)',
        color: '#2196F3',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'
        }
    }), [isDarkMode]);

    const editButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
        color: '#FF9800',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'
        }
    }), [isDarkMode]);

    const deleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
        color: '#F44336',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'
        }
    }), [isDarkMode]);

    // Mark All Complete button styles - Green
    const markCompleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
        color: '#4CAF50',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.1)' : 'rgba(158, 158, 158, 0.1)',
            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
        }
    }), [isDarkMode]);

    // Status badge component - Updated to match session management styling
    const getStatusBadge = (status: string) => {
        return (
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    color: getStatusColor(status || 'pending'),
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: status === 'completed'
                        ? (isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.2)')
                        : (status === 'in-progress' || status === 'in_progress')
                            ? (isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.2)')
                            : status === 'cancelled'
                                ? (isDarkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.2)')
                                : (isDarkMode ? 'rgba(158, 158, 158, 0.1)' : 'rgba(158, 158, 158, 0.2)'),
                    display: 'inline-block',
                    whiteSpace: 'nowrap'
                }}
            >
                {getStatusText(status || 'pending')}
            </Typography>
        );
    };

    // Mobile card component - CLICKABLE for navigation
    const WorkshopCard = ({ workshop }: { workshop: Workshop }) => (
        <Card
            onClick={() => {
                // Only navigate on desktop, not on mobile/tablet
                if (!isMobile && !isTablet) {
                    handleViewWorkshop(workshop);
                }
            }}
            sx={{
                mb: 2,
                boxShadow: 2,
                borderRadius: 2,
                backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : `1px solid ${theme.palette.divider}`,
                cursor: isMobile || isTablet ? 'default' : 'pointer',
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: isDarkMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.04)'
                },
                '&:active': {
                    transform: 'translateY(0px)',
                    transition: 'transform 0.1s ease'
                }
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                {/* Header Section - Title + Status + Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                fontSize: '1.1rem',
                                lineHeight: 1.3,
                                color: theme.palette.text.primary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: isMobile || isTablet ? 'default' : 'pointer',
                                '&:hover': {
                                    color: isMobile || isTablet ? theme.palette.text.primary : '#64B5F6',
                                    textDecoration: isMobile || isTablet ? 'none' : 'underline'
                                }
                            }}
                            onClick={() => {
                                // Only navigate on desktop, not on mobile/tablet
                                if (!isMobile && !isTablet) {
                                console.log('Workshop title clicked, navigating to workshop tasks for workshop:', workshop.id);
                                navigate(`/management-index/workshops/tasks/${workshop.id}`);
                                }
                            }}
                        >
                            {workshop.title}
                        </Typography>
                        {getStatusBadge(workshop.status)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                        <Tooltip title="View Workshop">
                            <IconButton
                                size="small"
                                disableRipple
                                onClick={() => handleViewWorkshop(workshop)}
                                sx={viewButtonStyles}
                            >
                                <ViewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {(user?.role === 'Admin' ||
                          (user?.role === 'Program Manager' && workshop.created_by_id === user?.id)) && (
                            <>
                                <Tooltip title="Edit Workshop">
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        onClick={() => handleEditWorkshop(workshop)}
                                        sx={editButtonStyles}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Workshop">
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        onClick={() => handleDeleteWorkshop(workshop)}
                                        sx={deleteButtonStyles}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {workshop.status !== 'completed' && (
                                    <Tooltip title="Mark All Tasks Complete">
                                        <IconButton
                                            size="small"
                                            disableRipple
                                            onClick={() => handleMarkAllComplete(workshop)}
                                            sx={markCompleteButtonStyles}
                                        >
                                            <DoneAllIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Details Section - HORIZONTAL LAYOUT (UNIFORM) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Description Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Description:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                            {workshop.description}
                        </Typography>
                    </Box>

                    {/* Assigned To Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Assigned To:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            {workshop.assigned_to}
                        </Typography>
                    </Box>

                    {/* Schedule Date Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Schedule Date:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            {formatTableDate(workshop.due_date)}
                        </Typography>
                    </Box>

                    {/* Created At Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Created:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            {formatTableDate(workshop.created_at)}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    const [workshops, setWorkshops] = useState<Workshop[]>([]);

    // Get user data from localStorage as fallback
    const getCurrentUser = () => {
        if (user) {
            return user;
        }

        // Fallback to localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                // Check if it's the old format (with username/password)
                if (parsedUser.username && parsedUser.password) {
                    // Convert old format to new format
                    return {
                        id: 'local-user-id',
                        email: parsedUser.username,
                        firstName: 'User',
                        lastName: 'Name',
                        name: 'User Name',
                        role: 'admin',
                        createdAt: new Date().toISOString()
                    };
                }
                // If it's already in the correct format, return as is
                return parsedUser;
            } catch (error) {
                console.error('❌ Error parsing user from localStorage:', error);
                return null;
            }
        }
        return null;
    };

    // Debug user data (same as session management)
    useEffect(() => {
        console.log('🔍 WorkshopsPage - User from auth store:', user);
        console.log('🔍 WorkshopsPage - User details:', {
            id: user?.id,
            name: user?.name,
            email: user?.email,
            role: user?.role
        });

        // Check if user is properly loaded
        if (!user) {
            console.warn('⚠️ No user found in auth store! Checking localStorage...');
            const storedUser = localStorage.getItem('user');
            const authToken = localStorage.getItem('authToken');
            console.log('⚠️ localStorage data:', { storedUser: !!storedUser, authToken: !!authToken });

            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    console.log('⚠️ Parsed user from localStorage:', parsedUser);
                } catch (error) {
                    console.error('❌ Error parsing user from localStorage:', error);
                }
            }
        }
    }, [user]);

    // Close sidebar when workshop page loads (mobile)
    useEffect(() => {
        if (isMobile) {
            // Dispatch a custom event to close sidebar
            const closeSidebarEvent = new CustomEvent('closeSidebar');
            window.dispatchEvent(closeSidebarEvent);
        }
    }, [location.pathname, isMobile]);

    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [assignedToFilter, setAssignedToFilter] = useState('');
    const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [workshopToDelete, setWorkshopToDelete] = useState<Workshop | null>(null);
    const [users, setUsers] = useState<Array<{ id: string, name: string, email: string, role_name: string }>>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Mark All Complete dialog state
    const [markCompleteDialogOpen, setMarkCompleteDialogOpen] = useState(false);
    const [workshopToMarkComplete, setWorkshopToMarkComplete] = useState<Workshop | null>(null);
    const [taskSummary, setTaskSummary] = useState<{
        pending: number;
        inProgress: number;
        completed: number;
        cancelled: number;
        total: number;
        toBeUpdated: number;
    } | null>(null);
    const [markCompleteLoading, setMarkCompleteLoading] = useState(false);
    const [taskSummaryLoading, setTaskSummaryLoading] = useState(false);

    // Pre-defined programs that cannot be edited or deleted
    const predefinedPrograms = [
        'Pre-Event Planning',
        'Content & Materials',
        'Logistics & Coordination',
        'Event Day Execution',
        'Post-Event Follow-up'
    ];

    const isPredefinedProgram = (title: string) => {
        return predefinedPrograms.includes(title);
    };

    // Fetch data from backend
    useEffect(() => {
        const fetchWorkshops = async () => {
            try {
                setLoading(true);

                // Fetch users for dropdown
                console.log('🔄 Fetching users from backend API...');
                setUsersLoading(true);
                try {
                    const usersData = await httpClient.get('/users');
                    console.log('📊 Users API Response Data:', usersData);

                    if (usersData.success && usersData.users && Array.isArray(usersData.users)) {
                        console.log('👥 Users fetched from API:', usersData.users.length, 'users');
                        setUsers(usersData.users);
                    } else {
                        console.log('❌ Users API failed or no data:', usersData);
                        setUsers([]);
                    }
                } catch (error) {
                    console.error('❌ Error fetching users:', error);
                    setUsers([]);
                } finally {
                    setUsersLoading(false);
                }

                // Fetch workshop programs directly (no need for complex combination)
                console.log('🔄 Fetching workshop programs from backend API...');
                const programsData = await httpClient.get('/programs/workshops');
                console.log('📊 Workshop Programs API Response:', programsData);

                const allWorkshops: Workshop[] = [];

                // Use workshop programs directly (already filtered by backend)
                if (programsData.success && programsData.programs) {
                    programsData.programs.forEach((program: any) => {
                        allWorkshops.push({
                            id: parseInt(program.id),
                            title: program.title,
                            description: program.description,
                            assigned_to: program.assignedTo,
                            due_date: program.dueDate,
                            created_at: program.createdAt,
                            updated_at: program.updatedAt,
                            status: program.status,
                            created_by_id: program.createdById,
                            created_by_role: program.creatorRole
                        });
                    });
                }

                // Sort workshops: predefined programs in serial order, new programs at top
                allWorkshops.sort((a, b) => {
                    const aIsPredefined = isPredefinedProgram(a.title);
                    const bIsPredefined = isPredefinedProgram(b.title);

                    // If both are predefined, sort by predefined order
                    if (aIsPredefined && bIsPredefined) {
                        const aIndex = predefinedPrograms.indexOf(a.title);
                        const bIndex = predefinedPrograms.indexOf(b.title);
                        return aIndex - bIndex;
                    }

                    // If both are new programs, sort by created_at (newest first)
                    if (!aIsPredefined && !bIsPredefined) {
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                    }

                    // New programs come before predefined programs
                    if (!aIsPredefined && bIsPredefined) {
                        return -1;
                    }

                    // Predefined programs come after new programs
                    if (aIsPredefined && !bIsPredefined) {
                        return 1;
                    }

                    return 0;
                });

                // DO NOT add programs data - only checklist categories

                console.log('✅ Data processed successfully:', allWorkshops);
                setWorkshops(allWorkshops);
                setLoading(false);
            } catch (error) {
                console.error('❌ Error fetching workshops:', error);
                console.log('🔄 Falling back to empty array...');
                setWorkshops([]);
                setLoading(false);
            }
        };

        fetchWorkshops();
    }, []);

    const filteredWorkshops = workshops.filter(workshop => {
        const matchesSearch = workshop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workshop.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workshop.assigned_to.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !statusFilter || workshop.status === statusFilter ||
            (statusFilter === 'in-progress' && workshop.status === 'in_progress') ||
            (statusFilter === 'in_progress' && workshop.status === 'in-progress');
        const matchesAssignedTo = !assignedToFilter || workshop.assigned_to === assignedToFilter;

        return matchesSearch && matchesStatus && matchesAssignedTo;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return '#4CAF50'; // Green
            case 'in-progress':
            case 'in_progress':
                return '#FF9800'; // Orange
            case 'cancelled':
                return '#f44336'; // Red
            case 'pending':
            default:
                return theme.palette.text.secondary; // Gray
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'completed':
                return 'COMPLETED';
            case 'in-progress':
            case 'in_progress':
                return 'IN-PROGRESS';
            case 'cancelled':
                return 'CANCELLED';
            case 'pending':
            default:
                return 'PENDING';
        }
    };

    const handleCreateWorkshop = () => {
        setDialogMode('create');
        setSelectedWorkshop({
            id: 0,
            title: '',
            description: '',
            assigned_to: '',
            due_date: '',
            created_at: '',
            updated_at: '',
            status: 'pending',
            created_by_id: '',
            created_by_role: ''
        });
        setDialogOpen(true);
    };

    const handleEditWorkshop = (workshop: Workshop) => {
        setDialogMode('edit');
        setSelectedWorkshop(workshop);
        setDialogOpen(true);
    };

    const handleViewWorkshop = (workshop: Workshop) => {
        // Navigate to specific program tasks page with program type
        // Map workshop type to program type for URL
        const programTypeMapping: { [key: string]: string } = {
            'Pre-Event Planning': 'planning',
            'Content & Materials': 'preparation',
            'Logistics & Coordination': 'logistics',
            'Event Day Execution': 'execution',
            'Post-Event Follow-up': 'post-session'
        };

        const programType = programTypeMapping[workshop.title] || 'planning';
        navigate(`/management-index/workshops/${programType}?programId=${workshop.id}`);
    };

    const handleDeleteWorkshop = (workshop: Workshop) => {
        console.log('🗑️ Delete button clicked for workshop:', workshop.title);
        setWorkshopToDelete(workshop);
        setDeleteDialogOpen(true);
        console.log('🗑️ Delete dialog should now be open');
    };


    const confirmDeleteWorkshop = async () => {
        if (workshopToDelete) {
            try {
                console.log('🗑️ Deleting workshop via backend API...');

                // Call backend API to delete program (workshop)
                await httpClient.delete(`/programs/${workshopToDelete.id}`);
                console.log('✅ Workshop deleted successfully from backend');

                // Close dialog first
                setDeleteDialogOpen(false);
                setWorkshopToDelete(null);

                // Refresh data from backend instead of local state update
                console.log('🔄 Refreshing workshops data from backend...');
                const refreshData = await httpClient.get('/programs/workshops');
                if (refreshData.success && refreshData.programs) {
                    const allWorkshops: Workshop[] = refreshData.programs.map((program: any) => ({
                        id: parseInt(program.id),
                        title: program.title,
                        description: program.description,
                        assigned_to: program.assignedTo,
                        due_date: program.dueDate,
                        created_at: program.createdAt,
                        updated_at: program.updatedAt,
                        status: program.status,
                        created_by_id: program.createdById,
                        created_by_role: program.creatorRole
                    }));

                    // Apply the same sorting logic as in fetchWorkshops
                    allWorkshops.sort((a, b) => {
                        const aIsPredefined = isPredefinedProgram(a.title);
                        const bIsPredefined = isPredefinedProgram(b.title);

                        // If both are predefined, sort by predefined order
                        if (aIsPredefined && bIsPredefined) {
                            const aIndex = predefinedPrograms.indexOf(a.title);
                            const bIndex = predefinedPrograms.indexOf(b.title);
                            return aIndex - bIndex;
                        }

                        // If both are new programs, sort by created_at (newest first)
                        if (!aIsPredefined && !bIsPredefined) {
                            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                        }

                        // New programs come before predefined programs
                        if (!aIsPredefined && bIsPredefined) {
                            return -1;
                        }

                        // Predefined programs come after new programs
                        if (aIsPredefined && !bIsPredefined) {
                            return 1;
                        }

                        return 0;
                    });

                    setWorkshops(allWorkshops);
                    console.log('✅ Workshops data refreshed from backend with proper sorting');
                }

            } catch (error) {
                console.error('❌ Error deleting workshop:', error);
                alert('Failed to delete workshop. Please try again.');
            }
        }
    };

    const cancelDeleteWorkshop = () => {
        setDeleteDialogOpen(false);
        setWorkshopToDelete(null);
    };

    // Mark All Complete handlers
    const handleMarkAllComplete = async (workshop: Workshop) => {
        console.log('Mark all complete clicked for workshop:', workshop.title);
        setWorkshopToMarkComplete(workshop);
        setTaskSummaryLoading(true);
        setMarkCompleteDialogOpen(true);

        try {
            const summary = await apiService.getTaskSummary(workshop.id.toString());
            setTaskSummary(summary);
        } catch (error) {
            console.error('Error fetching task summary:', error);
            setTaskSummary({
                pending: 0,
                inProgress: 0,
                completed: 0,
                cancelled: 0,
                total: 0,
                toBeUpdated: 0
            });
        } finally {
            setTaskSummaryLoading(false);
        }
    };

    const confirmMarkAllComplete = async () => {
        if (!workshopToMarkComplete) return;

        setMarkCompleteLoading(true);
        try {
            console.log('Marking all tasks complete for workshop:', workshopToMarkComplete.id);
            const result = await apiService.markAllTasksComplete(workshopToMarkComplete.id.toString());
            console.log('Mark all complete result:', result);

            // Close dialog
            setMarkCompleteDialogOpen(false);
            setWorkshopToMarkComplete(null);
            setTaskSummary(null);

            // Refresh workshops data
            console.log('Refreshing workshops data...');
            const refreshData = await httpClient.get('/programs/workshops');
            if (refreshData.success && refreshData.programs) {
                const allWorkshops: Workshop[] = refreshData.programs.map((program: any) => ({
                    id: parseInt(program.id),
                    title: program.title,
                    description: program.description,
                    assigned_to: program.assignedTo,
                    due_date: program.dueDate,
                    created_at: program.createdAt,
                    updated_at: program.updatedAt,
                    status: program.status,
                    created_by_id: program.createdById,
                    created_by_role: program.creatorRole
                }));

                // Apply the same sorting logic
                allWorkshops.sort((a, b) => {
                    const aIsPredefined = isPredefinedProgram(a.title);
                    const bIsPredefined = isPredefinedProgram(b.title);

                    if (aIsPredefined && bIsPredefined) {
                        const aIndex = predefinedPrograms.indexOf(a.title);
                        const bIndex = predefinedPrograms.indexOf(b.title);
                        return aIndex - bIndex;
                    }
                    if (!aIsPredefined && !bIsPredefined) {
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                    }
                    if (!aIsPredefined && bIsPredefined) return -1;
                    if (aIsPredefined && !bIsPredefined) return 1;
                    return 0;
                });

                setWorkshops(allWorkshops);
                console.log('Workshops data refreshed after mark all complete');
            }
        } catch (error) {
            console.error('Error marking all tasks complete:', error);
            alert('Failed to mark all tasks as complete. Please try again.');
        } finally {
            setMarkCompleteLoading(false);
        }
    };

    const cancelMarkAllComplete = () => {
        setMarkCompleteDialogOpen(false);
        setWorkshopToMarkComplete(null);
        setTaskSummary(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{
                width: '100%',
                flex: 1,
                p: { xs: 2, sm: 3 },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box sx={{
                    mb: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 2, md: 0 },
                    width: '100%'
                }}>
                    <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        width: { xs: '100%', md: 'auto' }
                    }}>
                        <Typography variant="h4" component="h1" sx={{
                            color: theme.palette.text.primary,
                            mb: 1,
                            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                            fontWeight: 'bold',
                            lineHeight: 1.2
                        }}>
                            Workshop Management
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                            lineHeight: 1.4
                        }}>
                            Manage and track your training workshops and programs
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: { xs: '100%', md: 'auto' },
                        flexShrink: 0
                    }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateWorkshop}
                            sx={{
                                backgroundColor: '#2196F3',
                                minWidth: { xs: '100%', sm: 'auto' },
                                height: { xs: '48px', sm: '56px' },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                '&:hover': {
                                    backgroundColor: '#1976D2',
                                },
                                '&:active': {
                                    transform: 'scale(0.98)',
                                    transition: 'transform 0.1s ease',
                                },
                                // Touch-friendly interactions
                                '@media (hover: none) and (pointer: coarse)': {
                                    '&:hover': {
                                        backgroundColor: '#2196F3',
                                    },
                                    '&:active': {
                                        backgroundColor: '#1976D2',
                                        transform: 'scale(0.95)',
                                    },
                                },
                            }}
                        >
                            Add Workshop
                        </Button>
                    </Box>
                </Box>

                {/* Summary Cards */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mb: 3
                }}>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{
                            width: '100%',
                            height: { xs: '120px', sm: '160px' },
                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(33, 150, 243, 0.3)',
                            borderRadius: { xs: 2, sm: 3 },
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 20px rgba(33, 150, 243, 0.2)',
                                transition: 'all 0.2s ease-in-out'
                            },
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h4" component="div" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                        }}>
                                            {workshops.length}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            Total Programs
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        borderRadius: '50%',
                                        background: 'rgba(33, 150, 243, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}>📋</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Card sx={{
                            width: '100%',
                            height: { xs: '120px', sm: '160px' },
                            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.1) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 152, 0, 0.3)',
                            borderRadius: { xs: 2, sm: 3 },
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 20px rgba(255, 152, 0, 0.2)',
                                transition: 'all 0.2s ease-in-out'
                            },
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h4" component="div" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                        }}>
                                            {workshops.filter(w => w.status === 'in_progress' || w.status === 'in-progress').length}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            In Progress
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        borderRadius: '50%',
                                        background: 'rgba(255, 152, 0, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}>🔄</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Card sx={{
                            width: '100%',
                            height: { xs: '120px', sm: '160px' },
                            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.1) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(156, 39, 176, 0.3)',
                            borderRadius: { xs: 2, sm: 3 },
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 20px rgba(156, 39, 176, 0.2)',
                                transition: 'all 0.2s ease-in-out'
                            },
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h4" component="div" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                        }}>
                                            {workshops.filter(w => w.status === 'pending').length}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            Pending
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        borderRadius: '50%',
                                        background: 'rgba(156, 39, 176, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}>⏳</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Card sx={{
                            width: '100%',
                            height: { xs: '120px', sm: '160px' },
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            borderRadius: { xs: 2, sm: 3 },
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.2)',
                                transition: 'all 0.2s ease-in-out'
                            },
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h4" component="div" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                        }}>
                                            {workshops.filter(w => w.status === 'completed').length}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            Completed
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        borderRadius: '50%',
                                        background: 'rgba(76, 175, 80, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}>✅</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>


                {/* Filters */}
                <Card sx={{
                    mb: 3,
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                }}>
                    <CardContent>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 2 },
                            alignItems: 'stretch'
                        }}>
                            <Box sx={{
                                width: { xs: '100%', sm: '300px' },
                                flex: { xs: 'none', sm: '1 1 300px' }
                            }}>
                                <TextField
                                    fullWidth
                                    label="Search workshops..."
                                    placeholder="Enter workshop name or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                            '& fieldset': { borderColor: theme.palette.divider },
                                            '&:hover fieldset': { borderColor: theme.palette.text.primary },
                                            '&.Mui-focused fieldset': { borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                            '&.Mui-focused': {
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{
                                width: { xs: '100%', sm: '200px' },
                                flex: { xs: 'none', sm: '1 1 200px' }
                            }}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        shrink={true}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                backgroundColor: 'transparent',
                                                padding: '0 8px',
                                                color: theme.palette.text.primary,
                                            },
                                        }}
                                    >Status</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        label="Status"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em style={{ color: theme.palette.text.secondary }}>Select status</em>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.text.primary
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="in-progress">In Progress</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>


                            <Box sx={{
                                width: { xs: '100%', sm: '200px' },
                                flex: { xs: 'none', sm: '1 1 200px' }
                            }}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        shrink={true}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                backgroundColor: 'transparent',
                                                padding: '0 8px',
                                                color: theme.palette.text.primary,
                                            },
                                        }}
                                    >Assigned To</InputLabel>
                                    <Select
                                        value={assignedToFilter}
                                        onChange={(e) => setAssignedToFilter(e.target.value)}
                                        label="Assigned To"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em style={{ color: theme.palette.text.secondary }}>Select assignee</em>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.text.primary
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Assignees</MenuItem>
                                        {users.map((user) => (
                                            <MenuItem key={user.id} value={user.name}>
                                                {user.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={async () => {
                                    console.log('🔄 Manual refresh button clicked');
                                    try {
                                        setLoading(true);
                                        console.log('🔄 Refreshing data from backend API...');

                                        // Fetch workshop programs directly (no need for complex combination)
                                        console.log('🔄 Refreshing workshop programs from backend API...');
                                        const programsData = await httpClient.get('/programs/workshops');
                                        console.log('📊 Workshop Programs API Response:', programsData);
                                        console.log('📊 Programs data structure:', {
                                            success: programsData.success,
                                            hasPrograms: !!programsData.programs,
                                            programsLength: programsData.programs?.length || 0,
                                            programsData: programsData.programs
                                        });

                                        const allWorkshops: Workshop[] = [];

                                        // Use workshop programs directly (already filtered by backend)
                                        if (programsData.success && programsData.programs) {
                                            programsData.programs.forEach((program: any) => {
                                                allWorkshops.push({
                                                    id: parseInt(program.id),
                                                    title: program.title,
                                                    description: program.description,
                                                    assigned_to: program.assignedTo,
                                                    due_date: program.dueDate,
                                                    created_at: program.createdAt,
                                                    updated_at: program.updatedAt,
                                                    status: program.status,
                                                    created_by_id: program.createdById,
                                                    created_by_role: program.creatorRole
                                                });
                                            });
                                        } else if (programsData.success && programsData.data && programsData.data.programs) {
                                            // Fallback for different API response structure
                                            console.log('🔄 Using fallback data structure...');
                                            programsData.data.programs.forEach((program: any) => {
                                                allWorkshops.push({
                                                    id: parseInt(program.id),
                                                    title: program.title,
                                                    description: program.description,
                                                    assigned_to: program.assignedTo,
                                                    due_date: program.dueDate,
                                                    created_at: program.createdAt,
                                                    updated_at: program.updatedAt,
                                                    status: program.status,
                                                    created_by_id: program.createdById,
                                                    created_by_role: program.creatorRole
                                                });
                                            });
                                        } else {
                                            console.warn('⚠️ No programs data found in API response');
                                            console.log('⚠️ Full API response:', programsData);
                                        }

                                        // Sort workshops: predefined programs in serial order, new programs at top
                                        allWorkshops.sort((a, b) => {
                                            const aIsPredefined = isPredefinedProgram(a.title);
                                            const bIsPredefined = isPredefinedProgram(b.title);

                                            // If both are predefined, sort by predefined order
                                            if (aIsPredefined && bIsPredefined) {
                                                const aIndex = predefinedPrograms.indexOf(a.title);
                                                const bIndex = predefinedPrograms.indexOf(b.title);
                                                return aIndex - bIndex;
                                            }

                                            // If both are new programs, sort by created_at (newest first)
                                            if (!aIsPredefined && !bIsPredefined) {
                                                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                                            }

                                            // New programs come before predefined programs
                                            if (!aIsPredefined && bIsPredefined) {
                                                return -1;
                                            }

                                            // Predefined programs come after new programs
                                            if (aIsPredefined && !bIsPredefined) {
                                                return 1;
                                            }

                                            return 0;
                                        });

                                        console.log('✅ Data refreshed successfully:', allWorkshops);
                                        setWorkshops(allWorkshops);
                                        setLoading(false);
                                    } catch (error) {
                                        console.error('❌ Error refreshing workshops:', error);
                                        setWorkshops([]);
                                        setLoading(false);
                                    }
                                }}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    borderColor: theme.palette.divider,
                                    height: '56px',
                                    px: 3,
                                    fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                    '&:hover': {
                                        borderColor: theme.palette.text.primary,
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Mobile Card Layout */}
                <Box sx={{
                    display: { xs: 'block', lg: 'none' },
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0
                }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary, ml: 2 }}>
                                Loading workshops...
                            </Typography>
                        </Box>
                    ) : filteredWorkshops.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                                No workshops found
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Try adjusting your filters or check back later.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ px: 1 }}>
                            {filteredWorkshops.map((workshop) => (
                                <WorkshopCard key={workshop.id} workshop={workshop} />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Responsive Table for all devices */}
                <Box sx={{
                    display: { xs: 'none', lg: 'block' },
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0
                }}>
                    <Card sx={{
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                    }}>
                        <TableContainer sx={{
                            overflow: 'auto',
                            maxHeight: { xs: '60vh', sm: '65vh', md: '70vh', lg: '80vh' },
                            width: '100%',
                            position: 'relative',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '3px',
                                '&:hover': {
                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                                },
                            },
                        }}>
                            <Table sx={{
                                tableLayout: 'auto',
                                width: '100%',
                                minWidth: { sm: '600px', md: '800px', lg: '1000px' },
                                '& .MuiTableCell-root': {
                                    padding: {
                                        xs: '6px 4px',
                                        sm: '8px 6px',
                                        md: '10px 8px',
                                        lg: '12px 10px'
                                    },
                                    fontSize: {
                                        xs: '0.875rem',
                                        sm: '0.9rem',
                                        md: '0.95rem',
                                        lg: '1rem'
                                    },
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '25%', md: '20%', lg: '18%' },
                                            minWidth: { sm: '120px', md: '150px', lg: '180px' },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}>Title</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '20%', md: '18%', lg: '16%' },
                                            minWidth: { sm: '100px', md: '120px', lg: '140px' },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}>Description</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '15%', md: '14%', lg: '12%' },
                                            minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}>Assigned To</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '10%', md: '8%', lg: '6%' },
                                            minWidth: { sm: '60px', md: '70px', lg: '80px' },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}>Status</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '12%', md: '10%', lg: '8%' },
                                            minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                            display: { xs: 'none', sm: 'none', md: 'table-cell' }
                                        }}>Schedule Date</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '12%', md: '10%', lg: '8%' },
                                            minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                            display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                        }}>Created At</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '12%', md: '10%', lg: '8%' },
                                            minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                            display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                        }}>Updated At</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '12%', md: '10%', lg: '8%' },
                                            minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                            display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                        }}>Created By</TableCell>
                                        <TableCell sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            fontSize: { sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                            width: { sm: '8%', md: '6%', lg: '5%' },
                                            minWidth: { sm: '60px', md: '70px', lg: '80px' },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredWorkshops.map((workshop) => (
                                        <TableRow
                                            key={workshop.id}
                                            hover
                                            sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}
                                        >
                                            <TableCell sx={{
                                                width: { sm: '25%', md: '20%', lg: '18%' },
                                                minWidth: { sm: '120px', md: '150px', lg: '180px' },
                                                display: { xs: 'none', sm: 'table-cell' }
                                            }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        fontWeight: 'bold',
                                                        fontSize: { sm: '0.85rem', md: '0.9rem', lg: '0.95rem' },
                                                        cursor: 'pointer',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        '&:hover': {
                                                            color: '#4CAF50',
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                    onClick={() => handleViewWorkshop(workshop)}
                                                >
                                                    {workshop.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '20%', md: '18%', lg: '16%' },
                                                minWidth: { sm: '100px', md: '120px', lg: '140px' },
                                                display: { xs: 'none', sm: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { sm: '0.8rem', md: '0.85rem', lg: '0.9rem' }
                                                }}>
                                                    {workshop.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '15%', md: '14%', lg: '12%' },
                                                minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                                display: { xs: 'none', sm: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.primary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { sm: '0.8rem', md: '0.85rem', lg: '0.9rem' }
                                                }}>
                                                    {workshop.assigned_to}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '10%', md: '8%', lg: '6%' },
                                                minWidth: { sm: '60px', md: '70px', lg: '80px' },
                                                display: { xs: 'none', sm: 'table-cell' }
                                            }}>
                                                {getStatusBadge(workshop.status)}
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '12%', md: '10%', lg: '8%' },
                                                minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                                display: { xs: 'none', sm: 'none', md: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: { md: '0.8rem', lg: '0.85rem' }
                                                }}>
                                                    {workshop.due_date ? new Date(workshop.due_date.includes('Z') ? workshop.due_date : workshop.due_date + 'Z').toLocaleDateString() : 'Not scheduled'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '12%', md: '10%', lg: '8%' },
                                                minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                                display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: { lg: '0.8rem' }
                                                }}>
                                                    {formatTableDate(workshop.created_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '12%', md: '10%', lg: '8%' },
                                                minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                                display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: { lg: '0.8rem' }
                                                }}>
                                                    {formatTableDate(workshop.updated_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '12%', md: '10%', lg: '8%' },
                                                minWidth: { sm: '80px', md: '100px', lg: '120px' },
                                                display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { lg: '0.8rem' }
                                                }}>
                                                    {workshop.created_by_role}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { sm: '8%', md: '6%', lg: '5%' },
                                                minWidth: { sm: '60px', md: '70px', lg: '80px' },
                                                display: { xs: 'none', sm: 'table-cell' }
                                            }}>
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title="View">
                                                        <IconButton
                                                            size="small"
                                                            disableRipple
                                                            onClick={() => handleViewWorkshop(workshop)}
                                                            sx={viewButtonStyles}
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {(user?.role === 'Admin' ||
                                                      (user?.role === 'Program Manager' && workshop.created_by_id === user?.id)) && (
                                                        <>
                                                            <Tooltip title="Edit Workshop">
                                                                <IconButton
                                                                    size="small"
                                                                    disableRipple
                                                                    onClick={() => handleEditWorkshop(workshop)}
                                                                    sx={editButtonStyles}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Workshop">
                                                                <IconButton
                                                                    size="small"
                                                                    disableRipple
                                                                    onClick={() => handleDeleteWorkshop(workshop)}
                                                                    sx={deleteButtonStyles}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {workshop.status !== 'completed' && (
                                                                <Tooltip title="Mark All Tasks Complete">
                                                                    <IconButton
                                                                        size="small"
                                                                        disableRipple
                                                                        onClick={() => handleMarkAllComplete(workshop)}
                                                                        sx={markCompleteButtonStyles}
                                                                    >
                                                                        <DoneAllIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Box>

                {/* Mobile and Tablet Cards View - REMOVED DUPLICATE LAYOUT */}
                {/* Now using only WorkshopCard component for consistency */}


                {/* Create/Edit Workshop Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                        {dialogMode === 'create' ? 'Create New Workshop' : 'Edit Workshop'}
                    </DialogTitle>
                    <DialogContent sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary
                    }}>
                        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Workshop Title"
                                    placeholder="Enter workshop title"
                                    value={selectedWorkshop?.title || ''}
                                    onChange={(e) => setSelectedWorkshop(prev => prev ? { ...prev, title: e.target.value } : null)}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    placeholder="Enter workshop description"
                                    multiline
                                    rows={3}
                                    value={selectedWorkshop?.description || ''}
                                    onChange={(e) => setSelectedWorkshop(prev => prev ? { ...prev, description: e.target.value } : null)}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <FormControl fullWidth>
                                    <InputLabel
                                        shrink={true}
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                backgroundColor: theme.palette.background.default,
                                                padding: '0 8px',
                                            },
                                        }}
                                    >
                                        Assigned To
                                    </InputLabel>
                                    <Select
                                        value={selectedWorkshop?.assigned_to || ''}
                                        onChange={(e) => setSelectedWorkshop(prev => prev ? { ...prev, assigned_to: e.target.value } : null)}
                                        label="Assigned To"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em style={{ color: theme.palette.text.secondary }}>Select assignee</em>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme.palette.background.default,
                                                    color: theme.palette.text.primary,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            Select a user
                                        </MenuItem>
                                        {usersLoading ? (
                                            <MenuItem value="" disabled>
                                                Loading users...
                                            </MenuItem>
                                        ) : users.length > 0 ? (
                                            users.map((user) => (
                                                <MenuItem key={user.id} value={user.name}>
                                                    {user.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem value="" disabled>
                                                No users available
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Schedule Date"
                                    placeholder="Select schedule date"
                                    type="date"
                                    value={selectedWorkshop?.due_date || ''}
                                    onChange={(e) => setSelectedWorkshop(prev => prev ? { ...prev, due_date: e.target.value } : null)}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                            cursor: 'pointer',
                                            opacity: 0.7,
                                            '&:hover': {
                                                opacity: 1
                                            }
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        padding: '16px 24px'
                    }}>
                        <Button
                            onClick={() => {
                                setDialogOpen(false);
                                setSelectedWorkshop(null);
                            }}
                            variant="outlined"
                            sx={{
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.primary,
                                borderWidth: 1.5,
                                '&:hover': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                    borderWidth: 1.5
                                },
                                '&:focus': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                    outline: 'none'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                if (dialogMode === 'create') {
                                    try {
                                        console.log('🚀 Creating new workshop via backend API...');

                                        // Get current user (from auth store or localStorage fallback)
                                        const currentUser = getCurrentUser();
                                        console.log('👤 User data:', currentUser);

                                        // Check if user is available (same as session management)
                                        if (!currentUser) {
                                            console.error('❌ No user found! User is not logged in.');
                                            alert('Please log in to create a program.');
                                            return;
                                        }

                                        // Prepare data for backend API (same approach as session management)
                                        const workshopData = {
                                            title: selectedWorkshop?.title || '',
                                            description: selectedWorkshop?.description || '',
                                            assignedTo: selectedWorkshop?.assigned_to || '',
                                            dueDate: selectedWorkshop?.due_date || '',
                                            programType: 'workshop',
                                            creatorId: currentUser?.id || null,
                                            creatorName: currentUser?.name || 'Unknown'
                                        };

                                        console.log('📝 Workshop data to send:', workshopData);
                                        console.log('📝 Workshop data validation:', {
                                            hasCreatorId: !!workshopData.creatorId,
                                            hasCreatorName: !!workshopData.creatorName,
                                            creatorIdValue: workshopData.creatorId,
                                            creatorNameValue: workshopData.creatorName
                                        });
                                        console.log('📝 Current user data:', {
                                            currentUser: currentUser,
                                            userId: currentUser?.id,
                                            userName: currentUser?.name,
                                            userEmail: currentUser?.email
                                        });

                                        // Check if user is properly loaded
                                        if (!currentUser || !currentUser.id) {
                                            console.error('❌ User not properly loaded!');
                                            console.log('❌ Current user state:', user);
                                            alert('User not properly loaded. Please refresh the page and try again.');
                                            return;
                                        }

                                        // Validate required fields
                                        if (!selectedWorkshop?.title || selectedWorkshop.title.trim() === '') {
                                            alert('Please enter a workshop title.');
                                            return;
                                        }
                                        if (!selectedWorkshop?.description || selectedWorkshop.description.trim() === '') {
                                            alert('Please enter a workshop description.');
                                            return;
                                        }

                                        // Use the same API service as session management
                                        console.log('🚀 Creating workshop using apiService...');
                                        const programDataWithCreator = {
                                            title: selectedWorkshop?.title || '',
                                            description: selectedWorkshop?.description || '',
                                            assignedTo: selectedWorkshop?.assigned_to || '',
                                            dueDate: selectedWorkshop?.due_date || '',
                                            name: selectedWorkshop?.title || '', // Add name property
                                            startDate: new Date().toISOString(), // Add startDate
                                            endDate: selectedWorkshop?.due_date || new Date().toISOString(), // Add endDate
                                            status: 'pending' as const, // Add status
                                            createdById: user?.id || undefined,
                                            createdByName: user?.name || 'Unknown',
                                            programType: 'workshop' // Workshop type as string for backend
                                        };

                                        console.log('➕ Workshop data with creator:', programDataWithCreator);
                                        const newProgram = await apiService.createProgram(programDataWithCreator);
                                        console.log('✅ New workshop created:', newProgram);

                                        // Close dialog first to prevent double loading
                                        setDialogOpen(false);
                                        setSelectedWorkshop(null);

                                        // Refresh data from backend instead of local state update
                                        console.log('🔄 Refreshing workshops data from backend...');
                                        const refreshData = await httpClient.get('/programs/workshops');
                                        if (refreshData.success && refreshData.programs) {
                                            const allWorkshops: Workshop[] = refreshData.programs.map((program: any) => ({
                                                id: parseInt(program.id),
                                                title: program.title,
                                                description: program.description,
                                                assigned_to: program.assignedTo,
                                                due_date: program.dueDate,
                                                created_at: program.createdAt,
                                                updated_at: program.updatedAt,
                                                status: program.status,
                                                created_by_id: program.createdById,
                                                created_by_role: program.creatorRole
                                            }));

                                            // Apply the same sorting logic as in fetchWorkshops
                                            allWorkshops.sort((a, b) => {
                                                const aIsPredefined = isPredefinedProgram(a.title);
                                                const bIsPredefined = isPredefinedProgram(b.title);

                                                // If both are predefined, sort by predefined order
                                                if (aIsPredefined && bIsPredefined) {
                                                    const aIndex = predefinedPrograms.indexOf(a.title);
                                                    const bIndex = predefinedPrograms.indexOf(b.title);
                                                    return aIndex - bIndex;
                                                }

                                                // If both are new programs, sort by created_at (newest first)
                                                if (!aIsPredefined && !bIsPredefined) {
                                                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                                                }

                                                // New programs come before predefined programs
                                                if (!aIsPredefined && bIsPredefined) {
                                                    return -1;
                                                }

                                                // Predefined programs come after new programs
                                                if (aIsPredefined && !bIsPredefined) {
                                                    return 1;
                                                }

                                                return 0;
                                            });

                                            setWorkshops(allWorkshops);
                                            console.log('✅ Workshops data refreshed from backend with proper sorting');
                                        }

                                    } catch (error) {
                                        console.error('❌ Error creating workshop:', error);
                                        console.error('❌ Error details:', {
                                            message: error instanceof Error ? error.message : 'Unknown error',
                                            stack: error instanceof Error ? error.stack : undefined,
                                            workshopData: {
                                                title: selectedWorkshop?.title,
                                                description: selectedWorkshop?.description,
                                                assignedTo: selectedWorkshop?.assigned_to,
                                                dueDate: selectedWorkshop?.due_date
                                            }
                                        });
                                        alert(`Failed to create workshop: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the console for details.`);
                                    }
                                } else {
                                    // Edit workshop logic
                                    try {
                                        console.log('🔄 Updating workshop via backend API...');

                                        // Use the same API service as session management for update
                                        console.log('🚀 Updating workshop using apiService...');
                                        const updateData = {
                                            title: selectedWorkshop?.title || '',
                                            description: selectedWorkshop?.description || '',
                                            assignedTo: selectedWorkshop?.assigned_to || '',
                                            dueDate: selectedWorkshop?.due_date || '',
                                        };

                                        console.log('📝 Update data to send:', updateData);

                                        const updatedProgram = await apiService.updateProgram(selectedWorkshop?.id.toString() || '', updateData);
                                        console.log('✅ Workshop updated successfully:', updatedProgram);

                                        // Close dialog first to prevent double loading
                                        setDialogOpen(false);
                                        setSelectedWorkshop(null);

                                        // Refresh data from backend instead of local state update
                                        console.log('🔄 Refreshing workshops data from backend...');
                                        const refreshData = await httpClient.get('/programs/workshops');
                                        if (refreshData.success && refreshData.programs) {
                                            const allWorkshops: Workshop[] = refreshData.programs.map((program: any) => ({
                                                id: parseInt(program.id),
                                                title: program.title,
                                                description: program.description,
                                                assigned_to: program.assignedTo,
                                                due_date: program.dueDate,
                                                created_at: program.createdAt,
                                                updated_at: program.updatedAt,
                                                status: program.status,
                                                created_by_id: program.createdById,
                                                created_by_role: program.creatorRole
                                            }));

                                            // Apply the same sorting logic as in fetchWorkshops
                                            allWorkshops.sort((a, b) => {
                                                const aIsPredefined = isPredefinedProgram(a.title);
                                                const bIsPredefined = isPredefinedProgram(b.title);

                                                // If both are predefined, sort by predefined order
                                                if (aIsPredefined && bIsPredefined) {
                                                    const aIndex = predefinedPrograms.indexOf(a.title);
                                                    const bIndex = predefinedPrograms.indexOf(b.title);
                                                    return aIndex - bIndex;
                                                }

                                                // If both are new programs, sort by created_at (newest first)
                                                if (!aIsPredefined && !bIsPredefined) {
                                                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                                                }

                                                // New programs come before predefined programs
                                                if (!aIsPredefined && bIsPredefined) {
                                                    return -1;
                                                }

                                                // Predefined programs come after new programs
                                                if (aIsPredefined && !bIsPredefined) {
                                                    return 1;
                                                }

                                                return 0;
                                            });

                                            setWorkshops(allWorkshops);
                                            console.log('✅ Workshops data refreshed from backend with proper sorting');
                                        }

                                    } catch (error) {
                                        console.error('❌ Error updating workshop:', error);
                                        alert('Failed to update workshop. Please try again.');
                                    }
                                }
                                setDialogOpen(false);
                            }}
                            variant="contained"
                            sx={{
                                color: 'white',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1565c0'
                                },
                                '&:focus': {
                                    outline: 'none'
                                }
                            }}
                        >
                            {dialogMode === 'create' ? 'Create' : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={cancelDeleteWorkshop}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            zIndex: 1300,
                            position: 'relative'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: '1.25rem',
                        fontWeight: 'bold'
                    }}>
                        🗑️ Confirm Delete
                    </DialogTitle>
                    <DialogContent sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        padding: '24px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{
                                backgroundColor: '#ffebee',
                                borderRadius: '50%',
                                p: 1,
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <DeleteIcon sx={{ color: '#f44336', fontSize: '24px' }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                                Delete Workshop
                            </Typography>
                        </Box>
                        <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
                            Are you sure you want to delete the workshop <strong>"{workshopToDelete?.title}"</strong>?
                        </Typography>
                        <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                            ⚠️ This action cannot be undone. All associated tasks and data will be permanently removed.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        pb: 2
                    }}>
                        <Button
                            onClick={cancelDeleteWorkshop}
                            variant="outlined"
                            sx={{
                                color: theme.palette.text.secondary,
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                    borderColor: theme.palette.text.primary,
                                    backgroundColor: theme.palette.action.hover,
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDeleteWorkshop}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.status.error,
                                '&:hover': {
                                    backgroundColor: '#d32f2f'
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Mark All Complete Confirmation Dialog */}
                <Dialog
                    open={markCompleteDialogOpen}
                    onClose={cancelMarkAllComplete}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            zIndex: 1300,
                            position: 'relative',
                            borderRadius: '12px'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <DoneAllIcon sx={{ color: '#4CAF50' }} />
                        Mark All Tasks Complete
                    </DialogTitle>
                    <DialogContent sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        padding: '24px'
                    }}>
                        {/* Workshop Title Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                borderRadius: '8px',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                border: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AssignmentIcon sx={{ color: theme.palette.primary.main, fontSize: '20px' }} />
                                <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                                    Workshop
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                                {workshopToMarkComplete?.title}
                            </Typography>
                        </Paper>

                        <Typography sx={{ color: theme.palette.text.primary, mb: 3 }}>
                            This will mark all pending and in-progress tasks as completed.
                        </Typography>

                        {/* Task Summary Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                border: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.primary }}>
                                Task Summary
                            </Typography>

                            {taskSummaryLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : taskSummary ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HourglassEmptyIcon sx={{ color: '#757575', fontSize: '18px' }} />
                                            <Typography sx={{ color: theme.palette.text.secondary }}>Pending</Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: 'bold', color: '#757575' }}>
                                            {taskSummary.pending} tasks
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LoopIcon sx={{ color: '#FF9800', fontSize: '18px' }} />
                                            <Typography sx={{ color: theme.palette.text.secondary }}>In Progress</Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                                            {taskSummary.inProgress} tasks
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '18px' }} />
                                            <Typography sx={{ color: theme.palette.text.secondary }}>Completed</Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                            {taskSummary.completed} tasks
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1,
                                        borderRadius: '4px',
                                        backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DoneAllIcon sx={{ color: '#4CAF50', fontSize: '18px' }} />
                                            <Typography sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                                Will be updated
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                            {taskSummary.toBeUpdated} tasks
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography sx={{ color: theme.palette.text.secondary }}>
                                    Unable to load task summary
                                </Typography>
                            )}
                        </Paper>

                        <Alert severity="warning" sx={{ mt: 3, borderRadius: '8px' }}>
                            This action cannot be undone. The workshop status will also be updated to "Completed".
                        </Alert>
                    </DialogContent>
                    <DialogActions sx={{
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        p: 2,
                        gap: 1
                    }}>
                        <Button
                            onClick={cancelMarkAllComplete}
                            variant="outlined"
                            disabled={markCompleteLoading}
                            sx={{
                                color: theme.palette.text.secondary,
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                    borderColor: theme.palette.text.primary,
                                    backgroundColor: theme.palette.action.hover,
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmMarkAllComplete}
                            variant="contained"
                            disabled={markCompleteLoading || taskSummaryLoading || (taskSummary?.toBeUpdated === 0)}
                            startIcon={markCompleteLoading ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
                            sx={{
                                backgroundColor: '#4CAF50',
                                '&:hover': {
                                    backgroundColor: '#388E3C'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.5)',
                                    color: 'white'
                                }
                            }}
                        >
                            {markCompleteLoading ? 'Processing...' : 'Mark Complete'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Floating Action Button */}
                <Fab
                    color="primary"
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 16, sm: 24 },
                        right: { xs: 16, sm: 24 },
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s ease-in-out'
                        },
                        '&:active': {
                            transform: 'scale(0.95)',
                            transition: 'transform 0.1s ease',
                        },
                        // Touch-friendly interactions
                        '@media (hover: none) and (pointer: coarse)': {
                            '&:hover': {
                                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                                transform: 'none',
                            },
                            '&:active': {
                                transform: 'scale(0.9)',
                            },
                        },
                        zIndex: 1000
                    }}
                    onClick={handleCreateWorkshop}
                >
                    <AddIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                </Fab>
            </Box>
        </Box>
    );
};

export default WorkshopsPage;

